import _ from 'underscore';

import {assert} from 'Asserts';
import {Grid} from 'constants/SampleConstants';
import {
  TransferStructuredTableDestinationType,
  TransferStructuredTableMetadata,
} from 'notebook/integrations/samples/TransferStructuredTableAdapter';
import {ColumnDefinition, updateCellOfColumnType} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {Cells} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {UpdateCellsTableOp} from 'notebook/tables/excess/ops/TableOpConstants';
import {TransferTableColumnGroup, TransferTableRow} from 'notebook/transfer-tables/TransferTableConstants';
import {
  getDestinationPlateOrBoxForRow,
  getNumberOfContainersForRow,
  getParentStorableForRow,
  getPositionsForRow,
  getPositionStringsForRow,
  StringResolutionError,
} from 'notebook/transfer-tables/TransferTableEditableRowsUtil';
import notifications from 'notifications';
import * as SampleUtil from 'samples/SampleUtil';

interface PositionsByGridId {
  [gridId: string]: Array<SampleUtil.Position>;
}
/**
 * Return an object mapping gridIds that are found in the table to a list of positions that have
 * been specified in the table for that grid.
 */

export function getTakenPositionsForGridsInTable({
  evaluatedEditableRows,
  columnDefinitions,
  destinationColumnDefinitionType,
}: {
  evaluatedEditableRows: Array<Array<EvaluatedCell>>;
  columnDefinitions: Array<ColumnDefinition>;
  destinationColumnDefinitionType?: TableColumnType;
}): PositionsByGridId {
  const takenPositionsByGridId: PositionsByGridId = {};
  for (const evaluatedEditableRow of evaluatedEditableRows) {
    let parentStorableSummary;

    if (
      destinationColumnDefinitionType == null ||
      destinationColumnDefinitionType === TableColumnType.PARENT_STORABLE
    ) {
      // Old transfer table uses parent storable column type
      parentStorableSummary = getParentStorableForRow(evaluatedEditableRow, columnDefinitions, {
        isRequired: false,
      });
    } else {
      parentStorableSummary = getDestinationPlateOrBoxForRow(
        evaluatedEditableRow,
        columnDefinitions,
        {isRequired: false},
        destinationColumnDefinitionType
      );
    }

    if (!parentStorableSummary) {
      continue;
    }

    const gridId = parentStorableSummary.id;
    let positions;
    try {
      positions = getPositionsForRow(
        evaluatedEditableRow,
        columnDefinitions,
        destinationColumnDefinitionType
      );
    } catch (err) {
      if (err instanceof StringResolutionError) {
        continue;
      } else {
        throw err;
      }
    }

    if (!takenPositionsByGridId[gridId]) {
      takenPositionsByGridId[gridId] = positions;
    } else {
      takenPositionsByGridId[gridId] = [...takenPositionsByGridId[gridId], ...positions];
    }
  }
  return takenPositionsByGridId;
}

/**
 * Return an object mapping gridIds to positions that are both available and not taken in the editable
 * rows of the table.
 */
export function getRemainingPositionsByGridId({
  availablePositionsByGridId,
  takenPositionsByGridId,
}: {
  availablePositionsByGridId: PositionsByGridId;
  takenPositionsByGridId: PositionsByGridId;
}): PositionsByGridId {
  const remainingPositionsByGridId: PositionsByGridId = {};
  for (const gridId of Object.keys(availablePositionsByGridId)) {
    const availablePositions = availablePositionsByGridId[gridId];
    const takenPositions = takenPositionsByGridId[gridId];

    if (!takenPositions) {
      remainingPositionsByGridId[gridId] = availablePositions;
    } else {
      remainingPositionsByGridId[gridId] = availablePositions.filter((position) => {
        const positionIsTaken = takenPositions.some(
          (takenPosition) =>
            takenPosition.rowIndex === position.rowIndex && takenPosition.columnIndex === position.columnIndex
        );
        return !positionIsTaken;
      });
    }
  }
  return remainingPositionsByGridId;
}

/**
 * Given a table represented by editableRows and columnDefinitions, return a new list of rows, where
 * the position cell has been filled out if possible with the next available positions in the corresponding
 * grid.
 *
 * TODO(BNCH-137192): Add table op support for grid position filldown in legacy transfer tables.
 */
export function fillNextAvailablePositions({
  editableRows,
  evaluatedEditableRows,
  gridsById,
  availablePositionsByGridId,
  columnDefinitions,
}: {
  editableRows: Array<TransferTableRow>;
  evaluatedEditableRows: Array<Array<EvaluatedCell>>;
  gridsById: {[id: string]: Grid | null};
  availablePositionsByGridId: PositionsByGridId;
  columnDefinitions: Array<ColumnDefinition>;
}): Array<TransferTableRow> {
  const numPositionsFilledSoFarByGridId = _.mapObject(gridsById, () => 0);
  const takenPositionsByGridId = getTakenPositionsForGridsInTable({evaluatedEditableRows, columnDefinitions});
  const remainingPositionsByGridId = getRemainingPositionsByGridId({
    availablePositionsByGridId,
    takenPositionsByGridId,
  });
  const newEditableRows = editableRows.slice();
  let isNoOp = true;

  for (const i of _.range(editableRows.length)) {
    const editableRow = editableRows[i];
    const evaluatedEditableRow = evaluatedEditableRows[i];
    const existingPositionStrings = getPositionStringsForRow(evaluatedEditableRow, columnDefinitions);
    if (existingPositionStrings.length !== 0) {
      // Ignore rows with existing positions
      continue;
    }

    let numberOfContainers;
    try {
      numberOfContainers = getNumberOfContainersForRow(evaluatedEditableRow, columnDefinitions, {
        isRequired: false,
      });
    } catch (err) {
      if (err instanceof StringResolutionError) {
        // Ignore rows without a valid number of containers
        continue;
      } else {
        throw err;
      }
    }

    if (!numberOfContainers) {
      // Ignore rows without a valid number of containers
      continue;
    }

    const parentStorableSummary = getParentStorableForRow(evaluatedEditableRow, columnDefinitions, {
      isRequired: false,
    });
    if (!parentStorableSummary) {
      // Ignore rows without a selected grid
      continue;
    }

    const gridId = parentStorableSummary.id;
    const startIndex = numPositionsFilledSoFarByGridId[gridId];
    const endIndex = startIndex + numberOfContainers;
    numPositionsFilledSoFarByGridId[gridId] += numberOfContainers;
    // Slice handles indices being out of bounds
    const nextAvailablePositions = remainingPositionsByGridId[gridId].slice(startIndex, endIndex);
    const positionStrings = nextAvailablePositions.map(({rowIndex, columnIndex}) =>
      SampleUtil.formatGridCoordinates(rowIndex, columnIndex)
    );
    const positionString = positionStrings.join(', ');
    const {newRow: newTableRow} = updateCellOfColumnType({
      columnGroup: TransferTableColumnGroup.TARGET,
      columnDefinitionType: TableColumnType.POSITION,
      tableColumnDefinitions: columnDefinitions,
      tableRow: editableRow.cells,
      cellValue: {text: positionString},
    });
    isNoOp = false;
    newEditableRows[i] = {...editableRow, cells: newTableRow};
  }
  if (isNoOp) {
    notifications.error('No available positions found.');
  }
  return newEditableRows;
}

export function fillNextEmptyPositions({
  cells,
  computedTable,
  tableMetadata,
  fillableGridIds,
  gridsById,
  availablePositionsByGridId,
  columnDefinitions,
  destinationColumnDefinitionType,
}: {
  cells: Cells;
  computedTable: Array<Array<EvaluatedCell>>;
  tableMetadata: TransferStructuredTableMetadata;
  fillableGridIds: Array<string>;
  gridsById: {[id: string]: Grid | null};
  availablePositionsByGridId: PositionsByGridId;
  columnDefinitions: Array<ColumnDefinition>;
  destinationColumnDefinitionType: TableColumnType;
}): {newCells: Cells; tableOp: UpdateCellsTableOp} {
  const tableOp: UpdateCellsTableOp = {type: 'update-cells', cells: []};
  const numPositionsFilledSoFarByGridId = _.mapObject(gridsById, () => 0); // initialize map
  const takenPositionsByGridId = getTakenPositionsForGridsInTable({
    evaluatedEditableRows: computedTable,
    columnDefinitions,
    destinationColumnDefinitionType,
  });
  const remainingPositionsByGridId = getRemainingPositionsByGridId({
    availablePositionsByGridId,
    takenPositionsByGridId,
  });
  let wereAnyGridsFull = false;

  // Fill each row with a position, keeping track of what positions have been used up
  for (const i of _.range(cells.length)) {
    const cellRow = cells[i];
    const computedRow = computedTable[i];
    const existingPositionStrings = getPositionStringsForRow(computedRow, columnDefinitions);
    if (existingPositionStrings.length !== 0) {
      continue;
    }
    const parentStorableSummary = getDestinationPlateOrBoxForRow(
      computedRow,
      columnDefinitions,
      {
        isRequired: false,
      },
      destinationColumnDefinitionType
    );
    if (!parentStorableSummary) {
      // Ignore rows without a grid
      continue;
    }

    // Only fill in grids that match table's destination type
    const gridId = fillableGridIds.includes(parentStorableSummary.id) ? parentStorableSummary.id : null;
    if (gridId == null) {
      continue;
    }

    const startIndex = numPositionsFilledSoFarByGridId[gridId];
    if (startIndex === remainingPositionsByGridId[gridId].length) {
      wereAnyGridsFull = true;
      continue;
    }
    numPositionsFilledSoFarByGridId[gridId]++;
    const nextAvailablePosition = remainingPositionsByGridId[gridId][startIndex];
    const positionString = SampleUtil.formatGridCoordinates(
      nextAvailablePosition.rowIndex,
      nextAvailablePosition.columnIndex
    );
    const {newRow: newTableRow, index: colIdx} = updateCellOfColumnType({
      columnGroup: TransferTableColumnGroup.TARGET,
      columnDefinitionType: TableColumnType.POSITION,
      tableColumnDefinitions: columnDefinitions,
      tableRow: cellRow,
      cellValue: {text: positionString},
    });
    cells[i] = newTableRow;
    tableOp.cells.push({row: i, column: colIdx});
  }
  if (wereAnyGridsFull) {
    assert(
      tableMetadata.destinationType !== TransferStructuredTableDestinationType.CONTAINER,
      'Destination type should be Plate or Box'
    );
    const destinationToPlural = {
      [TransferStructuredTableDestinationType.BOX]: 'boxes',
      [TransferStructuredTableDestinationType.MATRIX_PLATE]: 'plates',
      [TransferStructuredTableDestinationType.PLATE]: 'plates',
    };
    const string = `Some ${
      destinationToPlural[tableMetadata.destinationType]
    } are out of available positions.`;
    notifications.warn(string);
  }
  return {newCells: cells, tableOp};
}
